import { NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert';
import { ILandType, IPhoto, Listing } from '@curbnturf/entities';
import {
  CheckedComponent,
  DisplayValidationErrorsDirective,
  IPhotoForm,
  photoFormBuilder,
} from '@curbnturf/form-helpers';
import { listingComplete } from '@curbnturf/helpers';
import { HazardInputComponent } from '@curbnturf/host-forms/src/lib/hazard-input/hazard-input.component';
import { ListingTerrainComponent } from '@curbnturf/host-forms/src/lib/listing-terrain/listing-terrain.component';
import { ListingFacade } from '@curbnturf/listing';
import * as ListingActions from '@curbnturf/listing/src/lib/+state/listing.actions';
import { ListingPhotosComponent } from '@curbnturf/site-components/src/lib/listing-photos/listing-photos.component';
import { IonicModule } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-description',
  templateUrl: './description.component.html',
  styleUrls: ['./description.component.scss'],
  standalone: true,
  imports: [
    CheckedComponent,
    DisplayValidationErrorsDirective,
    FormsModule,
    IonicModule,
    HazardInputComponent,
    ListingPhotosComponent,
    ListingTerrainComponent,
    NgIf,
    ReactiveFormsModule,
  ],
})
export class DescriptionComponent implements OnDestroy, OnChanges, OnInit {
  @Input() listing: Listing;

  subscriptions: Subscription[] = [];

  form: FormGroup<{
    photos: FormArray<FormGroup<IPhotoForm>>;
  }>;

  potentialHazardsForm: FormGroup<{
    potentialHazards: FormControl<boolean | null>;
    potentialHazardDescription: FormControl<string | null>;
  }>;

  descriptionForm: FormGroup<{
    name: FormControl<string | null>;
    description: FormControl<string | null>;
  }>;

  landTypesForm: FormArray<FormControl<ILandType['type'] | null>>;

  listingComplete = listingComplete;

  get description() {
    if (this.form) {
      return this.descriptionForm.controls.description;
    }

    return;
  }

  get descriptionLength(): number {
    if (this.description) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((this.description as any)._pendingValue) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (this.description as any)._pendingValue.length;
      }

      return this.description.value?.length || 0;
    }

    return 0;
  }

  get landTypes(): FormArray<FormControl<ILandType['type'] | null>> {
    if (this.form) {
      return this.landTypesForm;
    }

    return this.fb.array<FormControl<ILandType['type'] | null>>([]);
  }

  get landTypesFormValue(): ILandType['type'][] {
    if (this.form) {
      return this.landTypesForm.controls.map((el) => {
        return el.value as ILandType['type'];
      });
    }
    return [];
  }

  get name() {
    if (this.form) {
      return this.descriptionForm.controls.name;
    }

    return;
  }

  constructor(
    private fb: FormBuilder,
    private listingFacade: ListingFacade,
    private alertFacade: AlertFacade,
    private actions$: Actions,
  ) {}

  ngOnInit() {
    this.makeForms();

    this.subscriptions.push(
      this.actions$.pipe(ofType(ListingActions.listingImagesUpdated)).subscribe((action) => {
        this.updatePhotos(action.photos);
      }),
    );
  }

  ngOnChanges() {
    this.makeForms();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  updatePhotos(photos: IPhoto[]) {
    this.form.controls.photos.clear();
    photos?.forEach((photo) => {
      this.form.controls.photos.push(photoFormBuilder(this.fb, photo));
    });
  }

  makeForms() {
    this.form = this.fb.group({
      photos: this.fb.array<FormGroup<IPhotoForm>>([]),
    });

    this.potentialHazardsForm = this.fb.group({
      potentialHazards: this.fb.control(false),
      potentialHazardDescription: this.fb.control(''),
    });

    this.descriptionForm = this.fb.group({
      name: this.fb.control<string | null>('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(32),
      ]),
      description: this.fb.control<string | null>('', [Validators.required, Validators.minLength(150)]),
    });

    this.landTypesForm = this.fb.array<FormControl<ILandType['type'] | null>>([]);

    if (this.listing) {
      this.form.patchValue({
        photos: [...(this.listing.photos ?? [])],
      });

      this.potentialHazardsForm.patchValue({
        potentialHazards: this.listing.potentialHazards ?? false,
        potentialHazardDescription: this.listing.potentialHazardDescription ?? '',
      });

      this.descriptionForm.patchValue({
        description: this.listing.description,
        name: this.listing.name,
      });

      this.updatePhotos(this.listing.photos ?? []);

      this.listing.landTypes?.forEach((land) => {
        this.landTypesForm.push(this.fb.control(land));
      });
    }

    this.subscriptions.push(
      this.descriptionForm.valueChanges
        .pipe(
          tap(() => {
            this.descriptionForm.markAsTouched();
            this.descriptionForm.markAsDirty();
            this.name?.markAsTouched();
            this.name?.markAsDirty();
            this.description?.markAsTouched();
            this.description?.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveDescription()),
      this.potentialHazardsForm.valueChanges
        .pipe(
          tap(() => {
            this.potentialHazardsForm.markAsTouched();
            this.potentialHazardsForm.markAsDirty();
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.savePotentialHazardsForm()),
      this.landTypesForm.valueChanges
        .pipe(
          tap(() => {
            this.landTypesForm.markAsTouched();
            this.landTypesForm.markAsDirty();
            this.landTypes.controls.forEach((control) => {
              control.markAsTouched();
              control.markAsDirty();
            });
          }),
          debounceTime(1000),
        )
        .subscribe(() => this.saveLandTypes()),
    );
  }

  saveDescription() {
    if (this.descriptionForm.valid && this.listing.id) {
      // Update the user record with the following data
      this.listingFacade.updateDescription(this.listing.id, {
        name: this.name?.value as string,
        description: this.description?.value === null ? undefined : this.description?.value,
      });
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  saveLandTypes() {
    if (this.landTypesForm.valid && this.listing.id) {
      // Update the user record with the following data
      this.listingFacade.updatePropertyTypes(
        this.listing.id,
        this.landTypes?.value?.filter((type) => type) as ILandType['type'][],
      );
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }

  savePotentialHazardsForm() {
    if (this.potentialHazardsForm.valid && this.listing.id) {
      // Update the user record with the following data
      const values = this.potentialHazardsForm.value;
      this.listingFacade.updatePotentialHazards(
        this.listing.id,
        values.potentialHazards || false,
        values.potentialHazardDescription || '',
      );
    } else {
      this.alertFacade.display({
        title: 'Form Incomplete',
        body: 'Check the form for errors before continuing.',
        level: 'warning',
      });
    }
  }
}
